import React from "react"

import { pluginComponents } from "./pluginComponents"
import { createPluginApi, PluginDataApi } from "./pluginDataApi"
import { pluginStyles } from "./pluginStyles"

// Error boundary component to prevent crashes
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error("Plugin Error:", error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="p-4 border border-red-200 rounded-lg bg-red-50">
            <h3 className="text-lg font-medium text-red-800">Plugin Error</h3>
            <p className="text-sm text-red-600 mt-2">
              {this.state.error?.message || "An error occurred in the plugin"}
            </p>
          </div>
        )
      )
    }

    return this.props.children
  }
}

// Create a safe proxy for component access
const createSafeComponentProxy = (components) => {
  // Create a fallback component factory that returns a visual placeholder
  const createFallbackComponent = (componentName) => {
    return (props) => (
      <div
        style={{
          padding: "8px",
          color: "#f87171",
          background: "#fee2e2",
          borderRadius: "4px",
          margin: "4px 0",
        }}
      >
        Component '{componentName}' is not available
      </div>
    )
  }

  // Create the safe proxy
  return new Proxy(components || {}, {
    get: (target, prop) => {
      // If the component exists, return it
      if (typeof prop === "string" && prop in target) {
        return target[prop]
      }

      // Otherwise, warn and return a fallback component
      if (typeof prop === "string") {
        console.warn(
          `Plugin tried to access undefined component: ${String(prop)}`,
        )
        return createFallbackComponent(String(prop))
      }

      return undefined
    },
  })
}

// Function to create an error component
const createErrorComponent = (errorMessage: string) => {
  return (props) => (
    <div className="p-4 border border-red-200 rounded-lg bg-red-50">
      <h3 className="text-lg font-medium text-red-800">Plugin Error</h3>
      <p className="text-sm text-red-600 mt-2">{errorMessage}</p>
    </div>
  )
}

// Function to wrap the component in an error boundary
const wrapWithErrorBoundary = (Component) => {
  return (props) => (
    <ErrorBoundary>
      <Component
        {...props}
        pluginComponents={props.pluginComponents}
        pluginStyles={props.pluginStyles}
        pluginApi={props.pluginApi}
      />
    </ErrorBoundary>
  )
}

/**
 * Creates a sandbox environment for running plugin code safely
 *
 * @param pluginCode - The transpiled plugin code to execute
 * @param contextData - Minimal context data for the plugin (ids, settings)
 * @param userRole - The user's role ("host", "guest", or "admin")
 * @param pluginDataApi - API for plugin data management (required)
 * @returns The sandboxed component or error information
 */
export const pluginSandbox = (
  pluginCode: string,
  contextData: {
    eventId: string
    userId: string
    userRole: string
    settings?: any
  },
  userRole: string,
  pluginDataApi?: PluginDataApi,
) => {
  try {
    // Create the plugin API with standardized methods
    const pluginApi = createPluginApi(pluginDataApi)

    // Create safe proxies for components and styles
    const safeComponents = createSafeComponentProxy(pluginComponents || {})
    const safeStyles = pluginStyles || {}

    // Create a sandboxed environment with a module pattern
    const sandbox = {
      React,
      component: null,
      exports: {}, // Add exports object for CommonJS style exports
      module: { exports: {} }, // Support module.exports pattern
      console: {
        log: (...args: any[]) => console.log("[Plugin]", ...args),
        error: (...args: any[]) => console.error("[Plugin]", ...args),
        warn: (...args: any[]) => console.warn("[Plugin]", ...args),
      },
      require: (module: string) => {
        // Simple require implementation
        if (module === "react") return React
        if (module === "react-dom") return require("react-dom")
        return {} // Return empty object for other modules
      },
      contextData,
      userRole,
      // Expose UI components and styles
      pluginComponents: safeComponents,
      pluginStyles: safeStyles,
      // Plugin data management APIs for access via pluginApi
      pluginApi,
      // Also expose to window for direct global access in plugins
      window: {
        pluginApi: {
          ...pluginApi,
          // Expose UI components and styles to window
          pluginComponents: safeComponents,
          pluginStyles: safeStyles,
        },
      },
    }

    // Function wrapper to evaluate the code
    const evaluateCode = new Function(
      "sandbox",
      `
      with (sandbox) {
        try {
        // Define global exports
        var exports = sandbox.exports;
        var module = sandbox.module;
        
        // Execute plugin code
        ${pluginCode}
        
        // Return the result
        return sandbox;
        } catch (error) {
          console.error("[Plugin Execution Error]", error);
          return { 
            error: true, 
            message: error instanceof Error ? error.message : String(error),
            sandbox: sandbox
          };
        }
      }
    `,
    )

    // Execute the code in the sandbox
    let evaluatedCode
    try {
      evaluatedCode = evaluateCode(sandbox)

      // Check if the evaluated code itself reported an error
      if (evaluatedCode && evaluatedCode.error === true) {
        console.error("Plugin code execution error:", evaluatedCode.message)
        return {
          component: createErrorComponent(evaluatedCode.message),
          error: true,
          message: evaluatedCode.message,
        }
      }
    } catch (error) {
      console.error("Error evaluating plugin code:", error)
      return {
        component: createErrorComponent(
          error instanceof Error ? error.message : String(error),
        ),
        error: true,
        message: error instanceof Error ? error.message : String(error),
      }
    }

    // Check for different export patterns and wrap with error boundary
    if (evaluatedCode.default && typeof evaluatedCode.default === "function") {
      // ES Module default export
      return {
        component: (props) =>
          wrapWithErrorBoundary(evaluatedCode.default)({
            ...props,
            pluginComponents: safeComponents,
            pluginStyles: safeStyles,
            pluginApi,
          }),
        error: false,
        message: "Success",
      }
    } else if (
      evaluatedCode.component &&
      typeof evaluatedCode.component === "function"
    ) {
      // Direct component export
      return {
        component: (props) =>
          wrapWithErrorBoundary(evaluatedCode.component)({
            ...props,
            pluginComponents: safeComponents,
            pluginStyles: safeStyles,
            pluginApi,
          }),
        error: false,
        message: "Success",
      }
    } else if (
      evaluatedCode.module &&
      evaluatedCode.module.exports &&
      typeof evaluatedCode.module.exports === "function"
    ) {
      // CommonJS export
      return {
        component: (props) =>
          wrapWithErrorBoundary(evaluatedCode.module.exports)({
            ...props,
            pluginComponents: safeComponents,
            pluginStyles: safeStyles,
            pluginApi,
          }),
        error: false,
        message: "Success",
      }
    } else if (
      evaluatedCode.module &&
      evaluatedCode.module.exports &&
      evaluatedCode.module.exports.default &&
      typeof evaluatedCode.module.exports.default === "function"
    ) {
      // Mixed CommonJS + ES module export
      return {
        component: (props) =>
          wrapWithErrorBoundary(evaluatedCode.module.exports.default)({
            ...props,
            pluginComponents: safeComponents,
            pluginStyles: safeStyles,
            pluginApi,
          }),
        error: false,
        message: "Success",
      }
    } else if (
      evaluatedCode.exports &&
      evaluatedCode.exports.default &&
      typeof evaluatedCode.exports.default === "function"
    ) {
      // Another ES module export variant
      return {
        component: (props) =>
          wrapWithErrorBoundary(evaluatedCode.exports.default)({
            ...props,
            pluginComponents: safeComponents,
            pluginStyles: safeStyles,
            pluginApi,
          }),
        error: false,
        message: "Success",
      }
    } else if (
      evaluatedCode.exports &&
      typeof evaluatedCode.exports === "function"
    ) {
      // Another CommonJS export variant
      return {
        component: (props) =>
          wrapWithErrorBoundary(evaluatedCode.exports)({
            ...props,
            pluginComponents: safeComponents,
            pluginStyles: safeStyles,
            pluginApi,
          }),
        error: false,
        message: "Success",
      }
    }

    // If we can't find a valid export, return error component
    return {
      component: createErrorComponent(
        "Could not find a valid React component export in the plugin code",
      ),
      error: true,
      message:
        "Could not find a valid React component export in the plugin code",
    }
  } catch (error) {
    console.error("Error in pluginSandbox:", error)
    const ErrorComponent = () => (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <h3 className="text-lg font-medium text-red-800">Plugin Error</h3>
        <p className="text-sm text-red-600 mt-2">
          {error instanceof Error ? error.message : String(error)}
        </p>
      </div>
    )

    return {
      component: ErrorComponent,
      error: true,
      message: error instanceof Error ? error.message : String(error),
    }
  }
}
